/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Users,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  AlertTriangle,
} from "lucide-react";
import "../styles/MPProfile.css";
import { fetchMPProfile } from "../api/policy_tracker/policyAPI";

const MPProfile = () => {
  const [expandedSpeech, setExpandedSpeech] = useState(null);
  const [expandedStance, setExpandedStance] = useState(null);
  const [mpData, setMpData] = useState(null);
  const { encodedMpID } = useParams();
  const mpID = decodeURIComponent(encodedMpID);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch MP data using the mpID
  useEffect(() => {
    const fetchMpData = async () => {
      try {
        setLoading(true);
        const delayDebounceFn = setTimeout(async () => {
          const result = await fetchMPProfile(mpID);
          setMpData(result);

          setLoading(false);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        // For development/testing, load sample data if API fails
        setMpData(sampleMpData);
        console.log(err.message);
      }
    };

    fetchMpData();
  }, [mpID]);

  const handleToggleSpeech = (index) => {
    if (expandedSpeech === index) {
      setExpandedSpeech(null);
    } else {
      setExpandedSpeech(index);
    }
  };

  const handleToggleStance = (index) => {
    if (expandedStance === index) {
      setExpandedStance(null);
    } else {
      setExpandedStance(index);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      Defense: "mp-profile-category-defense",
      Economy: "mp-profile-category-economy",
      Education: "mp-profile-category-education",
      Environment: "mp-profile-category-environment",
      "Foreign Affairs": "mp-profile-category-foreign-affairs",
      Healthcare: "mp-profile-category-healthcare",
      Immigration: "mp-profile-category-immigration",
      Infrastructure: "mp-profile-category-infrastructure",
      Justice: "mp-profile-category-justice",
      "Social Services": "mp-profile-category-social",
    };
    return colors[category] || "mp-profile-category-default";
  };

  const getConsistencyIcon = (alignment) => {
    if (alignment === "Consistent") {
      return <Check size={16} className="mp-profile-icon-consistent" />;
    } else if (alignment === "Inconsistent") {
      return <X size={16} className="mp-profile-icon-inconsistent" />;
    } else {
      return <AlertTriangle size={16} className="mp-profile-icon-mixed" />;
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") {
      return <span className="mp-profile-trend-up">↑</span>;
    } else if (trend === "down") {
      return <span className="mp-profile-trend-down">↓</span>;
    } else {
      return <span className="mp-profile-trend-same">→</span>;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="mp-profile-loading-container">Loading MP profile...</div>
    );
  }

  // Show error state
  if (error && !mpData) {
    return (
      <div className="mp-profile-error-container">
        Error loading MP profile: {error}
      </div>
    );
  }

  // If no data is available, show message
  if (!mpData) {
    return (
      <div className="mp-profile-error-container">
        No data available for this MP
      </div>
    );
  }

  return (
    <div className="mp-profile-container">
      {/* Header Section */}
      <div className="mp-profile-header">
        <div className="mp-profile-photo">
          {mpData.image ? (
            <img
              src={`https://www.openaustralia.org.au/${mpData.image}`}
              alt={`${mpData.name}`}
              className="mp-profile-photo-img"
            />
          ) : (
            <span className="mp-profile-photo-placeholder">MP Photo</span>
          )}
        </div>

        <div className="mp-profile-info">
          <h1 className="mp-profile-name">{mpData.name}</h1>
          <p className="mp-profile-position">{mpData.position}</p>
          <div className="mp-profile-details">
            <div className="mp-profile-detail">
              <MapPin size={18} className="mp-profile-detail-icon" />
              <span>{mpData.electorate}</span>
            </div>
            <div className="mp-profile-detail-separator">•</div>
            <div className="mp-profile-detail">
              <Users size={18} className="mp-profile-detail-icon" />
              <span>{mpData.party}</span>
            </div>
            <div className="mp-profile-detail-separator">•</div>
            <div className="mp-profile-detail">
              <Calendar size={18} className="mp-profile-detail-icon" />
              <span>Since {mpData.since}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Priority Topics */}
      <div className="mp-profile-section">
        <h2 className="mp-profile-section-title mp-profile-section-title-bordered">
          Priority Topics
        </h2>
        <p className="mp-profile-section-description">
          Based on parliamentary speeches, voting records, these are the MP's primary focus areas:
        </p>

        <div className="mp-profile-topics-container">
          {mpData.priorityTopics.map((topic, index) => (
            <div key={index} className="mp-profile-topic-item">
              <div className="mp-profile-topic-header">
                <div className="mp-profile-topic-title">
                  <h3 className="mp-profile-topic-name">{topic.POLICY_AREA}</h3>
                  {/* <span className="mp-profile-topic-trend">{getTrendIcon(topic.trend)}</span> */}
                </div>
                <div className="mp-profile-topic-percentage">
                  {topic.percentage}%
                </div>
              </div>
              <div className="mp-profile-topic-progress-bg">
                <div
                  className={`mp-profile-topic-progress-bar ${getCategoryColor(
                    topic.POLICY_AREA
                  )}`}
                  style={{ width: `${topic.percentage}%` }}
                  aria-label={`${topic.percentage}% focus on ${topic.POLICY_AREA}`}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Stances */}
      <div className="mp-profile-section">
        <h2 className="mp-profile-section-title mp-profile-section-title-bordered">
          Key Policy Positions
        </h2>

        <div className="mp-profile-stances-container">
          {mpData.keyStances.map((stance, index) => (
            <div key={index} className="mp-profile-stance-item">
              <div
                className="mp-profile-stance-header"
                onClick={() => handleToggleStance(index)}
              >
                <div className="mp-profile-stance-header-content">
                  <div className="mp-profile-stance-category">
                    <div
                      className={`mp-profile-category-indicator ${getCategoryColor(
                        stance.POLICY_AREA
                      )}`}
                    ></div>
                    <span className="mp-profile-category-name">
                      {stance.POLICY_AREA}
                    </span>
                  </div>
                  <h3 className="mp-profile-stance-issue">
                    {stance.topic_name}
                  </h3>
                  <div className="mp-profile-stance-summary">
                    <span className="mp-profile-stance-position">
                      {stance.stance}
                    </span>
                    <span className="mp-profile-stance-separator">•</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Voting Record */}
      <div className="mp-profile-section">
        <h2 className="mp-profile-section-title mp-profile-section-title-bordered">
          Recent Voting Record
        </h2>

        <div className="mp-profile-table-container">
          <table className="mp-profile-votes-table">
            <thead>
              <tr>
                <th>Bill</th>
                <th>Category</th>
                <th>Vote</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {mpData.recentVotes.map((vote, index) => (
                <tr key={index} className="mp-profile-vote-row">
                  <td className="mp-profile-vote-bill">{vote.bill_name}</td>
                  <td className="mp-profile-vote-category">
                    <span
                      className={`mp-profile-category-dot ${getCategoryColor(
                        vote.POLICY_AREA
                      )}`}
                    ></span>
                    {vote.POLICY_AREA}
                  </td>
                  <td className="mp-profile-vote-value">{vote.vote_result}</td>

                  <td className="mp-profile-vote-date">{vote.division_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Parliamentary Speeches */}
      <div className="mp-profile-section">
        <h2 className="mp-profile-section-title mp-profile-section-title-bordered">
          Recent Parliamentary Speeches
        </h2>

        <div className="mp-profile-speeches-container">
          {mpData.recentSpeeches.map((speech, index) => (
            <div key={index} className="mp-profile-speech-item">
              <div
                className="mp-profile-speech-header"
                onClick={() => handleToggleSpeech(index)}
              >
                <div className="mp-profile-speech-header-content">
                  <div className="mp-profile-speech-date">
                    <Calendar size={16} className="mp-profile-date-icon" />
                    <span>{speech.date}</span>
                  </div>
                  <h3 className="mp-profile-speech-title">
                    {speech.bill_name}
                  </h3>
                  <div className="mp-profile-speech-category">
                    <div
                      className={`mp-profile-category-indicator ${getCategoryColor(
                        speech.POLICY_AREA
                      )}`}
                    ></div>
                    <span>{speech.POLICY_AREA}</span>
                  </div>
                </div>
                {expandedSpeech === index ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </div>

              {expandedSpeech === index && (
                <div className="mp-profile-speech-details">
                  <p className="mp-profile-speech-excerpt">
                    {speech.speech_content.length > 800
                      ? `${speech.speech_content.substring(0, 800)}...`
                      : speech.speech_content}
                  </p>
                  <a
                    href={speech.url}
                    className="mp-profile-speech-read-more"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read Official Source
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="mp-profile-footer">
        <p>
          Source: Parliamentary records, voting analysis
        </p>
      </div>
    </div>
  );
};

// Sample data for testing when API is unavailable
const sampleMpData = {
  name: "Dr. Helen Haines MP",
  position: "Independent Member for Indi",
  electorate: "Indi, Victoria",
  party: "Independent",
  since: "2019",
  votingConsistency: 87,

  priorityTopics: [
    { topic: "Healthcare", percentage: 25, trend: "up" },
    { topic: "Housing", percentage: 20, trend: "up" },
    { topic: "Climate & Energy", percentage: 20, trend: "up" },
    { topic: "Regional Development", percentage: 15, trend: "same" },
    { topic: "Integrity", percentage: 10, trend: "same" },
    { topic: "Other", percentage: 10, trend: "down" },
  ],

  keyStances: [
    {
      issue: "Renewable Energy",
      stance: "Strong Support",
      consistency: "High",
      summary:
        "Champion for community-owned renewable energy projects and home batteries to reduce energy costs.",
      recentAction:
        "Introduced the Renewable Energy (Electricity) Amendment (Cheaper Home Batteries) Bill in March 2023.",
      category: "Environment",
    },
    {
      issue: "Regional Healthcare",
      stance: "Strong Support",
      consistency: "High",
      summary:
        "Advocates for better healthcare access in rural areas with focus on addressing workforce shortages.",
      recentAction:
        "Questioned the Health Minister in May 2023 about rural health workforce spending in the budget.",
      category: "Healthcare",
    },
    {
      issue: "Housing Affordability",
      stance: "Strong Support",
      consistency: "High",
      summary:
        "Advocates for increased social and affordable housing specifically tailored to regional areas.",
      recentAction:
        "Successfully amended the Housing Australia Future Fund Bill to explicitly consider regional Australia.",
      category: "Social Services",
    },
    {
      issue: "Government Integrity",
      stance: "Strong Support",
      consistency: "High",
      summary: "Vocal supporter of transparency and anti-corruption measures.",
      recentAction:
        "Serves as Deputy Chair of the Parliamentary Joint Committee on the National Anti-Corruption Commission.",
      category: "Justice",
    },
    {
      issue: "Indigenous Voice",
      stance: "Support",
      consistency: "High",
      summary:
        "Supports the Aboriginal and Torres Strait Islander Voice to Parliament.",
      recentAction:
        "Spoke in support of the Constitution Alteration bill in May 2023.",
      category: "Social Services",
    },
  ],

  recentVotes: [
    {
      bill: "Housing Australia Future Fund Bill 2023",
      voted: "For (with amendments)",
      alignment: "Consistent",
      date: "15 February 2023",
      category: "Social Services",
    },
    {
      bill: "Safeguard Mechanism (Crediting) Amendment Bill 2023",
      voted: "For (with concerns)",
      alignment: "Consistent",
      date: "21 March 2023",
      category: "Environment",
    },
    {
      bill: "Constitution Alteration (Aboriginal and Torres Strait Islander Voice) 2023",
      voted: "For",
      alignment: "Consistent",
      date: "24 May 2023",
      category: "Social Services",
    },
    {
      bill: "Infrastructure Australia Amendment (Independent Review) Bill 2023",
      voted: "For (with amendments)",
      alignment: "Consistent",
      date: "24 May 2023",
      category: "Economy",
    },
    {
      bill: "Inspector-General of Aged Care Bill 2023",
      voted: "For",
      alignment: "Consistent",
      date: "30 March 2023",
      category: "Healthcare",
    },
  ],

  recentSpeeches: [
    {
      date: "24 May 2023",
      title:
        "Constitution Alteration (Aboriginal and Torres Strait Islander Voice) 2023",
      excerpt:
        "I rise today to speak on the Constitution Alteration (Aboriginal and Torres Strait Islander Voice) 2023 bill. As the Member for Indi, I want to first acknowledge the traditional owners of the lands across my vast electorate...",
      policyArea: "Indigenous Affairs",
      category: "Social Services",
    },
    {
      date: "30 March 2023",
      title: "Inspector-General of Aged Care Bill 2023",
      excerpt:
        "I rise to speak in support of the Inspector-General of Aged Care Bill 2023. Today I'm thinking about Edie Dryden. Edie is the north-east's leading centenarian...",
      policyArea: "Aged Care Quality and Regulation",
      category: "Healthcare",
    },
    {
      date: "21 March 2023",
      title: "Safeguard Mechanism (Crediting) Amendment Bill 2023",
      excerpt:
        "The Safeguard Mechanism (Crediting) Amendment Bill 2022 presents an important opportunity for Australia to head in the right direction when it comes to reducing our emissions and acting on climate change...",
      policyArea: "Climate Change Mitigation",
      category: "Environment",
    },
  ],
};

export default MPProfile;
